import { useEffect, useRef, useState } from "react";
import { ReactComponent as SendLogo } from "../../Assets/arrow-up-solid.svg";
import { activateOnKey } from "../../Helper/a11y";
import { useConfigStore } from "../../Stores/Config";
import { DeleteIcon } from "../AnimatedIcons/AnimatedIcons";

interface AudioMessagePreviewProps {
  src: string;
  // Recorded length in ms, captured by the recorder — used for the total-time
  // readout and the progress denominator so we never depend on the blob's
  // metadata duration (which reports Infinity for webm recordings in Chrome).
  durationMs: number;
  isSending: boolean;
  onSend: () => void;
  onDelete: () => void;
}

const formatTime = (totalSeconds: number) => {
  const safe = Number.isFinite(totalSeconds) ? Math.max(0, totalSeconds) : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = Math.floor(safe % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// Static bar heights (0..1) for the preview waveform. Same center-peaked
// envelope as the live recording canvas (tall in the middle, tapering to dots
// at the edges) with a deterministic pseudo-random jitter so it reads as a real
// recorded clip rather than a smooth arc — and so the frozen preview looks like
// the recording view the moment before it, instead of a different widget.
const WAVEFORM_BARS = Array.from({ length: 42 }, (_, i) => {
  const t = i / 41;
  const envelope = Math.pow(Math.sin(t * Math.PI), 0.6);
  const noise = Math.sin(i * 12.9898) * 43758.5453;
  const rnd = noise - Math.floor(noise); // deterministic 0..1
  return Math.max(0.16, Math.min(1, 0.2 + envelope * (0.3 + 0.7 * rnd)));
});

const renderBars = () =>
  WAVEFORM_BARS.map((h, i) => (
    <span
      key={i}
      className="audio-message-preview-bar"
      style={{ height: `${Math.round(h * 100)}%` }}
    />
  ));

// On-brand preview of a finished recording — replaces the raw <audio controls>
// browser chrome. Mirrors the recording vie∏w so stopping doesn't "jump": a
// frozen waveform fills the top (with a play-progress fill sweeping across it),
// and a bottom-aligned controls row — [trash] … [time] [play] [send] — matches
// the recording state's [trash] … [time] [stop] [send].
const AudioMessagePreview = ({
  src,
  durationMs,
  isSending,
  onSend,
  onDelete,
}: AudioMessagePreviewProps) => {
  const translateTextWithFallback = useConfigStore(
    (state) => state.translateTextWithFallback,
  );
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const totalSeconds = durationMs / 1000;
  const progress =
    totalSeconds > 0 ? Math.min(100, (currentTime / totalSeconds) * 100) : 0;
  // Count up while playing, otherwise show the full length at rest.
  const displaySeconds =
    isPlaying || currentTime > 0 ? currentTime : totalSeconds;

  // Sample currentTime every frame while playing (the `timeupdate` event only
  // fires ~4×/second) so the progress fill sweeps smoothly instead of stepping.
  useEffect(() => {
    if (!isPlaying) {
      return;
    }
    let raf = 0;
    const tick = () => {
      const audio = audioRef.current;
      if (audio) {
        setCurrentTime(audio.currentTime);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);

  // Pause + rewind whenever the source changes (a new recording replaces an
  // old preview) or the component unmounts.
  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
      }
    };
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => {
        // Autoplay/gesture failures are non-fatal — the send button still works.
      });
    }
  };

  return (
    <div className="audio-message-preview">
      <div className="audio-message-preview-waveform" aria-hidden="true">
        {renderBars()}
        {/* Played overlay: an exact copy of the bars, clipped from the left to
            the current progress — a continuous (sub-bar) fill that sweeps
            smoothly rather than snapping whole bars at a time. */}
        <div
          className="audio-message-preview-fill"
          style={{ ["--progress" as any]: `${progress}%` }}
        >
          {renderBars()}
        </div>
      </div>
      <div className="audio-message-preview-controls">
        <DeleteIcon
          className="audio-recorder-icon audio-recorder-icon--trash"
          role="button"
          tabIndex={0}
          aria-label={translateTextWithFallback(
            "deleteRecording",
            "Delete recording",
          )}
          onClick={onDelete}
          onKeyDown={activateOnKey(onDelete)}
        />
        <div className="audio-message-preview-controls-right">
          <div className="audio-message-preview-time">
            {formatTime(displaySeconds)}
          </div>
          <button
            type="button"
            className="audio-message-preview-play"
            aria-label={isPlaying ? "Pause" : "Play"}
            onClick={togglePlay}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="6" y="5" width="4" height="14" rx="1.3" />
                <rect x="14" y="5" width="4" height="14" rx="1.3" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5.5v13a1 1 0 0 0 1.53.85l10-6.5a1 1 0 0 0 0-1.7l-10-6.5A1 1 0 0 0 8 5.5z" />
              </svg>
            )}
          </button>
          <div
            onClick={isSending ? undefined : onSend}
            onKeyDown={isSending ? undefined : activateOnKey(onSend)}
            className={`send-button ${isSending ? "send-button--busy" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={translateTextWithFallback(
              "sendRecording",
              "Send recording",
            )}
          >
            {isSending ? <div className="audio-spinner" /> : <SendLogo />}
          </div>
        </div>
      </div>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
      />
    </div>
  );
};

export default AudioMessagePreview;
